import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';
import { getBuildings } from '@/services/nocodeBackendService';

const { FiShield, FiWifi, FiCoffee, FiMapPin, FiArrowRight } = FiIcons;

const Home = () => {
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBuildings = async () => {
      try {
        const data = await getBuildings();
        setBuildings(data);
      } catch (error) {
        console.error('Error fetching buildings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBuildings();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <div className="bg-sky-50 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight mb-6">
            A Safe and Comfortable Home <br className="hidden md:block"/> for Students in Lucban
          </h1>
          <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto mb-10 leading-relaxed">
            Cloud's Boarding House provides affordable monthly bed-space accommodation 
            for students and approved young professionals in Mira Monte Subdivision, Lucban.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/accommodation" className="bg-blue-600 text-white px-8 py-3.5 rounded-lg font-semibold shadow-md hover:bg-blue-700 transition-all flex items-center justify-center gap-2">
              View Accommodation
              <SafeIcon icon={FiArrowRight} />
            </Link>
            <Link to="/contact" className="bg-white text-blue-600 border border-blue-200 px-8 py-3.5 rounded-lg font-semibold shadow-sm hover:bg-slate-50 transition-all">
              Contact Us
            </Link>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900">Student-Focused Benefits</h2>
            <p className="text-slate-500 mt-3">Designed to provide peace of mind for both students and parents.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: FiShield, title: 'Safe & Secure', desc: 'Secure premises ensuring a safe environment for all our residents.' },
              { icon: FiWifi, title: 'Study Friendly', desc: 'Quiet environment conducive for studying and academic focus.' },
              { icon: FiMapPin, title: 'Prime Location', desc: 'Conveniently located in Mira Monte Subdivision, accessible to universities.' }
            ].map((benefit, idx) => (
              <div key={idx} className="bg-slate-50 p-6 rounded-2xl border border-slate-100 text-center hover:shadow-md transition-shadow">
                <div className="w-14 h-14 bg-sky-100 text-sky-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={benefit.icon} className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-2">{benefit.title}</h3>
                <p className="text-slate-600">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Buildings Section */}
      <div className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900">Our Properties</h2>
            <p className="text-slate-500 mt-3">Explore our available buildings and monthly bed-space rates.</p>
          </div>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {buildings.map(building => (
                <div key={building.id} className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-lg transition-all flex flex-col">
                  <div className="h-48 bg-sky-100 flex items-center justify-center">
                    <SafeIcon icon={FiMapPin} className="w-12 h-12 text-sky-300" />
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">{building.name}</h3>
                    <p className="text-slate-600 mb-4 flex-1">{building.description}</p>
                    <div className="flex justify-between items-center bg-slate-50 p-4 rounded-xl mb-6">
                      <div>
                        <span className="block text-sm text-slate-500 font-medium">Monthly Rate</span>
                        <span className="text-xl font-bold text-blue-600">₱{building.monthly_rate}</span>
                      </div>
                      <div className="text-right">
                        <span className="block text-sm text-slate-500 font-medium">Capacity</span>
                        <span className="text-slate-800 font-semibold">{building.room_count} Rooms</span>
                      </div>
                    </div>
                    <Link to={`/accommodation/${building.id}`} className="w-full text-center bg-sky-50 text-sky-700 font-semibold py-3 rounded-lg hover:bg-sky-100 transition-colors">
                      View Availability
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Home;